import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';
import type { ComponentProps } from 'react';

import { Spinner } from '@platform/ui/components/spinner';
import { cn } from '@platform/ui/lib/utils';

/*
 * The one button (docs/10 §4). Normal is 44px — page actions, form submission,
 * calls to action. Small is 36px, for a table row, a card header or a filter
 * toolbar, and grows back to 44px on a touch screen. Destructive is
 * error-coloured text and edge, never a fill: a red block beside the orange
 * primary would read as a second primary.
 */
export const buttonVariants = cva(
    "inline-flex shrink-0 items-center justify-center gap-2 rounded-control border font-medium whitespace-nowrap transition-colors outline-none select-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-55 aria-disabled:pointer-events-none aria-disabled:opacity-55 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
    {
        variants: {
            variant: {
                primary: 'border-primary-button-border bg-primary-button font-semibold text-primary-button-foreground hover:bg-primary-button-hover',
                secondary: 'border-border-strong bg-card text-foreground hover:bg-accent',
                ghost: 'border-transparent bg-transparent text-muted-foreground hover:bg-accent hover:text-foreground',
                danger: 'border-destructive bg-card text-destructive-strong hover:bg-destructive-subtle',
                link: 'border-transparent bg-transparent text-primary-strong underline-offset-4 hover:underline',
            },
            size: {
                md: 'h-11 px-5 text-15',
                sm: 'h-9 px-3 text-13 pointer-coarse:h-11',
                icon: 'size-11',
                'icon-sm': 'size-9 pointer-coarse:size-11',
            },
            block: {
                true: 'w-full',
            },
        },
        compoundVariants: [{ variant: 'link', className: 'h-auto px-0' }],
        defaultVariants: {
            variant: 'primary',
            size: 'md',
        },
    },
);

export type ButtonVariant = NonNullable<VariantProps<typeof buttonVariants>['variant']>;
export type ButtonSize = NonNullable<VariantProps<typeof buttonVariants>['size']>;

export interface ButtonProps extends ComponentProps<'button'>, VariantProps<typeof buttonVariants> {
    /** Draws the one child — a router link, say — as the button. */
    asChild?: boolean;
    /** Shows a spinner and blocks interaction while the action is in flight. */
    loading?: boolean;
}

export function Button({ className, variant = 'primary', size = 'md', block, asChild = false, loading, disabled, children, ...props }: ButtonProps) {
    const Comp = asChild ? Slot.Root : 'button';
    return (
        <Comp
            data-slot="button"
            data-variant={variant}
            data-size={size}
            // A loading button must also be inert, or a double click submits the form twice.
            disabled={asChild ? undefined : disabled || loading}
            aria-busy={loading || undefined}
            {...props}
            className={cn(buttonVariants({ variant, size, block }), className)}
        >
            {asChild ? (
                children
            ) : (
                <>
                    {loading && <Spinner size="sm" />}
                    {children}
                </>
            )}
        </Comp>
    );
}
